import { useState } from 'react';
import type { Schemas } from '../api/client';
import { Icon, Modal, errorText } from '../ui/kit';
import { useSession } from '../auth/session';
import { isPlanLimit, PlanLimitNotice } from '../pages/billing/Subscription';

const CURRENCIES: [string, string][] = [['USD', 'US dollar'], ['ZAR', 'South African rand'], ['EUR', 'Euro'], ['GBP', 'British pound'], ['CAD', 'Canadian dollar'], ['MXN', 'Mexican peso']];

export type BusinessType = Schemas['BusinessType'];
export type Industry = Schemas['Industry'];
/** The fixed lists the database accepts for tenants.business_type and tenants.industry (migration 0018). */
export const BUSINESS_TYPES: BusinessType[] = ['LLC', 'Sole proprietorship', 'S corporation', 'C corporation', 'Partnership', 'Nonprofit'];
export const INDUSTRIES: Industry[] = ['Professional services', 'Retail', 'Construction and trades', 'Real estate', 'Food and hospitality', 'Other'];

/** Creates a business through POST /v1/tenants: the caller becomes its owner, with the standard US chart. */
export function AddBusinessModal({ onClose, onCreated }: { onClose: () => void; onCreated: (name: string) => void }) {
  const session = useSession();
  const [name, setName] = useState('');
  const [businessType, setBusinessType] = useState<BusinessType>('LLC');
  const [industry, setIndustry] = useState<Industry>('Professional services');
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState('');
  const [overLimit, setOverLimit] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = name.trim();
    if (!n) { setError('Enter the business name'); return; }
    if (session.tenants.some((t) => t.name.toLowerCase() === n.toLowerCase())) { setError('You already have a business with this name'); return; }
    setBusy(true);
    try {
      await session.createBusiness(n, currency, { business_type: businessType, industry });
      onCreated(n);
    } catch (e) {
      if (isPlanLimit(e)) setOverLimit(errorText(e));
      else setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Modal title="Add a business" onClose={onClose} onSubmit={submit}
      footer={<><span className="spacer" /><button type="button" className="btn ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn primary" disabled={busy}><Icon name="plus" />{busy ? 'Creating…' : 'Create business'}</button></>}>
      <p className="muted" style={{ fontSize: 13 }}>Each business has its own books, team and settings. You will be its owner, and you can switch between businesses from the menu at any time.</p>
      <div className="field"><label htmlFor="ab-name">Business name</label><input className={`input ${error ? 'bad' : ''}`} id="ab-name" maxLength={200} autoFocus value={name} onChange={(e) => { setName(e.target.value); setError(''); }} placeholder="Legal or trading name" aria-describedby="ab-name-err" /><span className="error" id="ab-name-err" role={error ? 'alert' : undefined}>{error}</span></div>
      <div className="form-grid">
        <div className="field"><label htmlFor="ab-type">Business type</label><select className="input" id="ab-type" value={businessType} onChange={(e) => setBusinessType(e.target.value as BusinessType)}>{BUSINESS_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}</select></div>
        <div className="field"><label htmlFor="ab-industry">Industry</label><select className="input" id="ab-industry" value={industry} onChange={(e) => setIndustry(e.target.value as Industry)}>{INDUSTRIES.map((v) => <option key={v} value={v}>{v}</option>)}</select></div>
        <div className="field span2"><label htmlFor="ab-currency">Currency</label><select className="input" id="ab-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>{CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} · {n}</option>)}</select><span className="hint">It cannot change once there are entries</span></div>
      </div>
      {overLimit && <PlanLimitNotice message={overLimit} />}
      <div className="notice good" style={{ margin: 0 }}><Icon name="check" size={17} /><div>MyBooks sets up the standard US chart of accounts, so you can invoice straight away.</div></div>
    </Modal>
  );
}
